import apiClient from "../api-client.js";

/**
 * Stripe billing endpoints (`billing/` app). Only agency_admin of an external
 * agency may call them. Every charge happens on a Stripe page: the endpoints
 * that change something return `{ url }` and the SPA syncs on return.
 */

export const BILLING_QUERY_KEY = ["billing"];
export const BILLING_DETAILS_QUERY_KEY = ["billing", "details"];
export const BILLING_MEMBERS_QUERY_KEY = ["billing", "members"];
export const BILLING_HOURS_QUERY_KEY = ["billing", "hours"];

const integerParam = (value, min) => {
  const number = Number(value);
  return value != null && value !== "" && Number.isInteger(number) && number >= min ? number : null;
};

// Query of a Checkout success/cancel URL (`checkout`, plus `plan`, `extra_users` or `hours`); null otherwise.
export const parseCheckoutReturn = (searchParams) => {
  const status = searchParams.get("checkout");
  if (status !== "success" && status !== "cancel") return null;
  const hours = integerParam(searchParams.get("hours"), 1);
  const extraUsers = integerParam(searchParams.get("extra_users"), 0);
  const plan = searchParams.get("plan") || null;
  const kind = hours != null ? "hours" : extraUsers != null ? "extra_users" : plan ? "plan" : null;
  return { status, kind, hours, extraUsers, plan };
};

export const getBilling = async () => {
  const response = await apiClient.get("billing/");
  return response.data;
};

// Re-reads plan, seats and paid hours Checkouts from Stripe (no webhooks locally). Returns the billing summary;
// SERSEO agencies get the hours balance instead ({ remaining_minutes, price, expires_on }).
// Re-reading Stripe can exceed the default 10 s; the caller's polling deadline bounds the wait
export const syncBilling = async () => {
  const response = await apiClient.post("billing/sync/", null, { timeout: 30000 });
  return response.data;
};

// Undoes a scheduled downgrade or cancellation of the plan or the seats. Returns the billing summary.
export const keepPlan = async (kind) => {
  const response = await apiClient.post("billing/keep-plan/", { kind });
  return response.data;
};

export const getBillingDetails = async () => {
  const response = await apiClient.get("billing/details/");
  return response.data;
};

export const saveBillingDetails = async (details) => {
  const response = await apiClient.put("billing/details/", details);
  return response.data;
};

// The following return the Stripe URL to redirect to (Checkout, portal confirmation or portal home).
export const createCheckoutSession = async (plan) => {
  const response = await apiClient.post("billing/checkout/", { plan });
  return response.data.url;
};

export const changePlan = async (plan) => {
  const response = await apiClient.post("billing/change-plan/", { plan });
  return response.data.url;
};

// Total paid extra seats; 0 cancels them at the period end.
export const setExtraUsers = async (quantity) => {
  const response = await apiClient.post("billing/extra-users/", { quantity });
  return response.data.url;
};

export const purchaseHours = async (hours) => {
  const response = await apiClient.post("billing/hours/", { hours });
  return response.data.url;
};

export const createPortalSession = async () => {
  const response = await apiClient.post("billing/portal/");
  return response.data.url;
};

// Active admins + consultants taking a seat: [{ id, name, email, role, removable }].
export const getBillingMembers = async () => {
  const response = await apiClient.get("billing/members/");
  return response.data;
};

// Team above the plan seats (users_over_limit > 0): deactivate exactly that many. Returns the billing summary.
export const releaseUsers = async (removeUserIds) => {
  const response = await apiClient.post("billing/release-users/", { remove_user_ids: removeUserIds });
  return response.data;
};

// { remaining_minutes, price: { amount, currency } | null, expires_on }. Open to every agency, SERSEO included.
export const getHoursSummary = async () => {
  const response = await apiClient.get("billing/hours/");
  return response.data;
};
